import {
  forwardRef, useEffect, useId, useImperativeHandle, useRef, useState,
  type InputHTMLAttributes, type KeyboardEvent, type MouseEvent,
} from 'react'
import { cx } from './cx'
import s from './ColorField.module.css'

export interface ColorFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'onChange' | 'type' | 'prefix'> {
  /** The colour, as `#rrggbb`. */
  value: string
  /** Called with a normalised `#rrggbb` — from the picker, or a valid typed hex. */
  onChange: (hex: string) => void
  /**
   * The colour's NAME ("Primary colour", "Gradient start"), required: it names
   * the typed hex value and the picker both, so neither is an unnamed control
   * when four of them share a panel.
   */
  label: string
}

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

/** `#abc`, `abc`, `#AABBCC` → `#aabbcc`; anything else → null. */
export function normalizeHex(text: string): string | null {
  const m = HEX.exec(text.trim())
  if (!m) return null
  const body = m[1]!.toLowerCase()
  return `#${body.length === 3 ? body.replace(/./g, (c) => c + c) : body}`
}

/**
 * A COLOUR, AS A VALUE SLOT (#2406 b3-inputs).
 *
 * The brand editor drew four bare `<input type="color">` wells — the browser's
 * own control, sized by a private class, no hex a person could read or type,
 * and on the gradient two of them named only by an `aria-label` the other two
 * lacked. This is the kit's colour control.
 *
 * ── one slot, two ways in ───────────────────────────────────────────────────
 * The SLOT is the wrapper, in the value-slot material (`Input.module.css`'s
 * contract, restated through `:has()` the way `UnitInput` restates it). Inside
 * it sit a SWATCH — a seated plate painted with the colour, holding the
 * native picker — and the HEX, a text input showing the value in a form a
 * person can read, copy and type. The native `<input type="color">` lives
 * INSIDE the swatch, transparent over it: the browser's picker is the one
 * that knows the platform's colour dialog, and the kit decides how it looks.
 *
 * ── naming and keyboard ─────────────────────────────────────────────────────
 * The HEX input is the control a `Field` wires: `id`, `aria-describedby`,
 * `aria-invalid` and `aria-required` arrive in `rest` and land on it, so the
 * field's label points at it. It is named "<label> hex value"; the picker is
 * named "Pick <label>" — both from `label`, so each is distinct on a panel of
 * four. Tab reaches the picker (Enter/Space opens the platform dialog) and then
 * the hex. Typing a full hex (`#0f766e`, `0f766e`) applies it at
 * once; Enter or leaving the field also accepts the short form and puts back
 * the colour in force when what is typed is not one. A character no hex can
 * hold marks the slot invalid (the crimson vein, and `aria-invalid`); an
 * unfinished value does not. Escape puts back the colour in force.
 */
export const ColorField = forwardRef<HTMLInputElement, ColorFieldProps>(function ColorField(
  { value, onChange, label, className, style, disabled, readOnly, onBlur, onKeyDown, ...rest },
  ref,
) {
  const hexInput = useRef<HTMLInputElement>(null)
  useImperativeHandle(ref, () => hexInput.current as HTMLInputElement)
  const auto = useId()
  const current = normalizeHex(value) ?? '#000000'
  const [draft, setDraft] = useState(current)
  /* An EXTERNAL change (the picker, a reset, another brand) replaces the
     draft; the echo of the person's own typing does not, or `#0f7` would be
     rewritten to `#00ff77` under their fingers. */
  useEffect(() => {
    setDraft((d) => (normalizeHex(d) === current ? d : current))
  }, [current])
  /* Incomplete is not invalid — `#0f7` on its way to `#0f766e` reads as
     ready. A character no hex can hold, or a length no hex has, is. */
  const impossible = !/^#?[0-9a-f]{0,6}$/i.test(draft.trim())
  const invalid = rest['aria-invalid'] === true || rest['aria-invalid'] === 'true' || impossible

  const apply = (hex: string) => { if (hex !== current) onChange(hex) }
  const type = (text: string) => {
    setDraft(text)
    if (/^#?[0-9a-f]{6}$/i.test(text.trim())) apply(normalizeHex(text)!)
  }
  /* Finishing (Enter, or leaving the field) accepts the short form too, and
     puts back the colour in force when what is typed is not a colour. */
  const settle = () => {
    const hex = normalizeHex(draft)
    if (hex) { apply(hex); setDraft(hex) } else setDraft(current)
  }

  const keys = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event)
    if (event.defaultPrevented) return
    if (event.key === 'Escape' && draft !== current) { event.preventDefault(); setDraft(current) }
    if (event.key === 'Enter') settle()
  }

  /* A press on the slot's own ground (the padding) focuses the hex, as it
     would on a plain field; the swatch opens the picker by itself. */
  const focusValue = (event: MouseEvent<HTMLSpanElement>) => {
    if (event.target !== event.currentTarget || disabled) return
    event.preventDefault()
    hexInput.current?.focus()
  }

  return (
    // The wrapper forwards a pointer press to the input it wraps; the inputs
    // are the controls and remain the keyboard path, so no role belongs here.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <span className={cx(s.slot, className)} style={style} data-color-field="" onMouseDown={focusValue}>
      <span className={s.swatch} style={{ backgroundColor: current }}>
        <input
          type="color"
          className={s.well}
          value={current}
          aria-label={`Pick ${label}`}
          disabled={disabled || readOnly}
          onChange={(e) => type(e.target.value)}
        />
      </span>
      <input
        {...rest}
        ref={hexInput}
        id={rest.id ?? `${auto}-hex`}
        type="text"
        className={s.hex}
        aria-label={`${label} hex value`}
        aria-invalid={invalid || undefined}
        value={draft}
        disabled={disabled}
        readOnly={readOnly}
        spellCheck={false}
        autoComplete="off"
        maxLength={7}
        placeholder="#rrggbb"
        onChange={(e) => type(e.target.value)}
        onKeyDown={keys}
        onBlur={(e) => { settle(); onBlur?.(e) }}
      />
    </span>
  )
})
